import React, {createContext, useContext, useState, useMemo, useCallback} from "react";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../entrypoints/theme";

// Create the context
const GlobalMenuContext = createContext(null);

// Create a single shared state
let globalState = {
    currentMenu: null,
    setCurrentMenu: () => {},
};

// Provider Component
export const GlobalMenuProvider = ({ children }) => {
    const isMobile = useMobileBreakpoint(mobileBreakpoint);
    // Only initialize state once (singleton pattern)
    if (!globalState.setCurrentMenu) {
        const [currentMenu, setCurrentMenu] = useState(null);
        globalState = { currentMenu, setCurrentMenu };
    }

    const [currentMenu, setCurrentMenu] = useState(null); // Tracks the currently active menu
    const [hoveredMenu, setHoveredMenu] = useState(null); // Tracks the menu currently hovered

    const openMenu = useCallback(
        (menuId) => {
            globalState.setCurrentMenu(menuId);

            if (isMobile) {
                document.body.classList.add(`${menuId.toLowerCase()}-open`);
            }

            // Simulate overlay functionality
            Alpine?.store("overlay")?.show();
        },
        [isMobile]
    );

    const closeMenu = useCallback(
        () => {
            const { currentMenu } = globalState;

            if (currentMenu && isMobile) {
                document.body.classList.remove(`${currentMenu.toLowerCase()}-open`);
            }

            globalState.setCurrentMenu(null);

            // Simulate overlay functionality
            Alpine?.store("overlay")?.hide();
        },
        [isMobile]
    );

    const contextValue = useMemo(
        () => ({
            currentMenu: globalState.currentMenu,
            openMenu,
            closeMenu,
            isMobile
        }),
        [globalState.currentMenu, openMenu, closeMenu, isMobile]
    );

    return (
        <GlobalMenuContext.Provider value={contextValue}>
            {children}
        </GlobalMenuContext.Provider>
    );
};

// Hook to use the global menu
export const globalMenuProvider = () => {
    const context = useContext(GlobalMenuContext);
    if (!context) {
        throw new Error("useGlobalMenu must be used within a GlobalMenuProvider");
    }
    return context;
};